import { useEffect, useRef, useCallback, useState } from "react";
import { Box } from "rebass/styled-components";
import styled from "styled-components";

import { useAds } from "MVVM/Providers";

interface EndSlateAdProps {
  slotId: string;
  game?: string;
  onAdLoaded?: (hasAd: boolean) => void;
}

const EndSlateAd = ({ slotId, game, onAdLoaded }: EndSlateAdProps) => {
  const { gpt, gptStatus, config } = useAds();
  const slotRef = useRef<typeof window.googletag.Slot | null>(null);
  const isInitializingRef = useRef(false);
  const listenerAddedRef = useRef(false);
  const [domReady, setDomReady] = useState(false);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setDomReady(true);
    }
  }, []);

  useEffect(() => {
    const displayConfig = config?.display;
    const isEnabled = displayConfig?.enabled === true;

    if (!gpt || gptStatus !== "ready") {
      onAdLoaded?.(false);
      return;
    }

    if (!displayConfig || !isEnabled) {
      onAdLoaded?.(false);
      return;
    }

    if (!domReady) {
      return;
    }

    if (slotRef.current || isInitializingRef.current) {
      return;
    }

    isInitializingRef.current = true;

    gpt.cmd.push(() => {
      if (slotRef.current) {
        isInitializingRef.current = false;
        return;
      }

      const adElement = document.getElementById(slotId);
      if (!adElement) {
        isInitializingRef.current = false;
        onAdLoaded?.(false);
        return;
      }

      const slot = gpt.defineSlot(
        `/${displayConfig.networkId}/${displayConfig.slateAdUnit}`,
        [[300, 168]],
        slotId,
      );

      if (!slot) {
        isInitializingRef.current = false;
        onAdLoaded?.(false);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const testAds = params.get("testAds");
      if (testAds) {
        slot.setTargeting("test", testAds);
      }

      if (game) {
        slot.setTargeting("game", game);
      }

      const viewerSource = window.location.href.includes("iframe")
        ? "iframe"
        : "website";
      slot.setTargeting("viewer_source", viewerSource);

      slot.addService(gpt.pubads());
      slotRef.current = slot;

      if (!listenerAddedRef.current) {
        listenerAddedRef.current = true;
        gpt
          .pubads()
          .addEventListener(
            "slotRenderEnded",
            (event: { slot: typeof slot; isEmpty: boolean }) => {
              if (event.slot === slotRef.current) {
                onAdLoaded?.(!event.isEmpty);
              }
            },
          );
      }

      gpt.display(slotId);
      gpt.pubads().refresh([slot]);
      isInitializingRef.current = false;
    });
  }, [gpt, gptStatus, config?.display, domReady, slotId, game, onAdLoaded]);

  useEffect(() => {
    const currentGpt = gpt;
    return () => {
      if (currentGpt && slotRef.current) {
        const slotToDestroy = slotRef.current;
        currentGpt.cmd.push(() => {
          currentGpt.destroySlots([slotToDestroy]);
        });
        slotRef.current = null;
      }
    };
  }, [gpt]);

  return <SlateContainer ref={containerRef} id={slotId} />;
};

const SlateContainer = styled(Box)`
  width: 300px;
  height: 168px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export { EndSlateAd };
